import { Group, Icon, InlineLink, Stack, Text, Title } from "@assetwatch/ui";
import { Card } from "@assetwatch/ui/experimental";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/sharp-solid-svg-icons";
import { useTranslation } from "react-i18next";
import type {
  ItemOfConcern,
  ItemsOfConcernResponse,
} from "../api/enterpriseClient";
import { panelHeaderStyles } from "./shared/panelHeader";
import { useOpenFacilityDrawer } from "./useOpenFacilityDrawer";

type Props = {
  data: ItemsOfConcernResponse;
  costsAtRisk: number;
  maintenanceRecommendations: number;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const metricClassName = "flex flex-col gap-0";
const metricNumberRowClassName = "flex items-center gap-(--spacing-8)";

function ConcernCard({ item }: { item: ItemOfConcern }) {
  const { t } = useTranslation();
  const openFacilityDrawer = useOpenFacilityDrawer();
  const awUpdate = item.nextStepText
    ? item.nextStepDaysOpen !== null
      ? t("enterprise.concerns.awUpdateDays", {
          days: item.nextStepDaysOpen,
          nextStep: item.nextStepText,
        })
      : t("enterprise.concerns.awUpdateNoDays", {
          nextStep: item.nextStepText,
        })
    : t("enterprise.concerns.awUpdateReviewing");

  return (
    <article className="flex flex-col gap-(--spacing-12) rounded-(--radius-lg) border border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-16)">
      <div className="flex flex-col gap-(--spacing-4) [&_p]:m-0">
        <Text as="div" size="sm">
          {item.machineName ? (
            <>
              <strong>{item.machineName}</strong>
              <span className="text-text-subtle">
                {" "}
                ·{" "}
                <InlineLink onClick={() => openFacilityDrawer(item.facilityId)}>
                  {item.facilityName}
                </InlineLink>
              </span>
            </>
          ) : (
            <InlineLink onClick={() => openFacilityDrawer(item.facilityId)}>
              <strong>{item.facilityName}</strong>
            </InlineLink>
          )}
        </Text>

        {item.shortObservation || item.observation ? (
          <Text size="sm">
            <strong>{t("enterprise.concerns.issueLabel")}</strong>{" "}
            {item.shortObservation || item.observation}
          </Text>
        ) : null}

        {item.shortRecommendation || item.recommendation ? (
          <Text size="sm">
            <strong>{t("enterprise.concerns.nextStepLabel")}</strong>{" "}
            {item.shortRecommendation || item.recommendation}
          </Text>
        ) : null}
      </div>

      <div className="rounded-(--radius-md) border-[1.5px] border-error/[0.5] bg-surface px-(--spacing-12) py-(--spacing-10) [&_p]:m-0">
        <Text size="sm">
          <strong>{t("enterprise.concerns.awUpdateLabel")}</strong> {awUpdate}
        </Text>
      </div>
    </article>
  );
}

export function ItemsOfConcernPanel({
  data,
  costsAtRisk,
  maintenanceRecommendations,
}: Props) {
  const { t } = useTranslation();

  return (
    <Card
      padding="none"
      className="px-(--spacing-20) py-(--spacing-16)"
      data-testid="items-of-concern-panel"
    >
      <Stack gap={20}>
        <div className={panelHeaderStyles.panelHeader}>
          <Group>
            <FontAwesomeIcon icon={faCircleExclamation} />
            <Title size="lg">{t("enterprise.concerns.panelTitle")}</Title>
          </Group>
        </div>

        <div className="flex flex-wrap gap-(--spacing-32) pb-(--spacing-8)">
          <div
            className={metricClassName}
            data-testid="items-of-concern-costs-at-risk"
          >
            <div className={metricNumberRowClassName}>
              <Icon name="error-solid" tone="negative" size="sm" aria-hidden />
              <Title>{formatCurrency(costsAtRisk)}</Title>
            </div>
            <Text as="div" size="sm" tone="subtle">
              {t("enterprise.concerns.metricCostsAtRisk")}
            </Text>
          </div>
          <div
            className={metricClassName}
            data-testid="items-of-concern-maintenance-recs"
          >
            <div className={metricNumberRowClassName}>
              <Icon name="maintenance" tone="negative" size="sm" aria-hidden />
              <Title>{maintenanceRecommendations.toLocaleString()}</Title>
            </div>
            <Text as="div" size="sm" tone="subtle">
              {t("enterprise.concerns.metricMaintenanceRecs")}
            </Text>
          </div>
        </div>

        {data.items.length === 0 ? (
          <Text tone="subtle" data-testid="items-of-concern-empty">
            {t("enterprise.concerns.emptyConcerns")}
          </Text>
        ) : (
          <Stack gap={12}>
            <Title size="md" as="h2">
              {t("enterprise.concerns.listHeading")}
            </Title>
            <div
              className="grid gap-(--spacing-12)"
              data-testid="items-of-concern-list"
            >
              {data.items.map((item) => (
                <ConcernCard key={item.alertId} item={item} />
              ))}
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
